import React, { useEffect, useState } from 'react';
import { BarChartIcon, UsersIcon, PackageIcon, TagIcon, ShieldAlertIcon, PlusIcon, PencilIcon, TrashIcon, SaveIcon, XIcon, MenuIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AdminService from '../services/adminService';
import { MenuItem, PromoIncentive, ComplaintFeedback } from '../types/database';
import { useDatabase } from '../contexts/DatabaseContext';
const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const {
    user,
    isLoggedIn
  } = useAuth();
  const navigate = useNavigate();
  const {
    isLoaded
  } = useDatabase();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Check if user is logged in and is admin
  useEffect(() => {
    if (!isLoggedIn || !user || user.User_type !== 'admin') {
      navigate('/login');
    }
  }, [isLoggedIn, user, navigate]);
  // State for orders
  const [orders, setOrders] = useState([]);
  // State for menu items
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  // State for promotions
  const [promotions, setPromotions] = useState<PromoIncentive[]>([]);
  // State for complaints
  const [complaints, setComplaints] = useState<ComplaintFeedback[]>([]);
  // State for users
  const [users, setUsers] = useState([]);
  // State for flagged accounts
  const [flaggedAccounts, setFlaggedAccounts] = useState([]);
  // State for editing
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [editingPromotion, setEditingPromotion] = useState<PromoIncentive | null>(null);
  // New state for order editing
  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [orderItems, setOrderItems] = useState<any[]>([]);
  const [availableMenuItems, setAvailableMenuItems] = useState<MenuItem[]>([]);
  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    todayOrderCount: 0,
    pendingOrderCount: 0,
    totalRevenue: 0,
    newCustomers: 0
  });
  // New item/promotion forms
  const [newItem, setNewItem] = useState({
    name: '',
    category: '',
    price: '',
    status: 'Available',
    image: '',
    description: ''
  });
  const [newPromotion, setNewPromotion] = useState({
    code: '',
    description: '',
    type: 'Discount',
    value: '',
    usageLimit: 'One-time',
    status: 'Active'
  });
  // Load order details for editing
  const loadOrderForEditing = async (orderId: string) => {
    try {
      const orderDetails = await AdminService.getOrderDetails(orderId);
      setEditingOrder(orderDetails);
      setOrderItems(orderDetails.items);
      const menuItems = await AdminService.getMenuItems();
      setAvailableMenuItems(menuItems);
    } catch (error) {
      console.error('Error loading order details:', error);
    }
  };
  const cancelOrderEditing = () => {
    setEditingOrder(null);
    setOrderItems([]);
    setAvailableMenuItems([]);
  };
  const updateOrderItemQuantity = async (orderDetailId: number, newQuantity: number) => {
    if (newQuantity < 1) {
      if (window.confirm('Remove this item from the order?')) {
        try {
          await AdminService.deleteOrderItem(orderDetailId);
          setOrderItems(orderItems.filter(item => item.OrderDetail_ID !== orderDetailId));
          const updatedOrder = await AdminService.getOrderDetails(editingOrder.OrderID);
          setEditingOrder(updatedOrder);
          // Refresh orders list
          const ordersData = await AdminService.getOrders();
          setOrders(ordersData);
        } catch (error) {
          console.error('Error removing item:', error);
          alert('Failed to remove item');
        }
      }
      return;
    }
    try {
      await AdminService.updateOrderItem(orderDetailId, newQuantity);
      setOrderItems(orderItems.map(item => item.OrderDetail_ID === orderDetailId ? {
        ...item,
        Quantity: newQuantity,
        Subtotal: item.price * newQuantity
      } : item));
      const updatedOrder = await AdminService.getOrderDetails(editingOrder.OrderID);
      setEditingOrder(updatedOrder);
      // Refresh orders list
      const ordersData = await AdminService.getOrders();
      setOrders(ordersData);
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert('Failed to update quantity');
    }
  };
  const addItemToOrder = async (itemId: number) => {
    try {
      await AdminService.addOrderItem(editingOrder.OrderID, itemId, 1);
      const updatedOrder = await AdminService.getOrderDetails(editingOrder.OrderID);
      setEditingOrder(updatedOrder);
      setOrderItems(updatedOrder.items);
      // Refresh orders list
      const ordersData = await AdminService.getOrders();
      setOrders(ordersData);
    } catch (error) {
      console.error('Error adding item:', error);
      alert('Failed to add item to order');
    }
  };
  const saveOrderChanges = async () => {
    alert('Order updated successfully!');
    cancelOrderEditing();
  };
  // Load data from database
  useEffect(() => {
    if (!isLoggedIn || !user || !isLoaded) return;
    const loadData = async () => {
      try {
        // Load dashboard stats
        const stats = await AdminService.getDashboardStats();
        setDashboardStats(stats);
        // Load orders
        const ordersData = await AdminService.getOrders();
        setOrders(ordersData);
        // Load menu items
        const menuItemsData = await AdminService.getMenuItems();
        setMenuItems(menuItemsData);
        // Load promotions
        const promotionsData = await AdminService.getPromotions();
        setPromotions(promotionsData);
        // Load complaints
        const complaintsData = await AdminService.getComplaints();
        setComplaints(complaintsData);
        // Load users
        const usersData = await AdminService.getUsers();
        setUsers(usersData);
        // For flagged accounts, we'll keep using the sample data for now
        const savedFlaggedAccounts = localStorage.getItem('flaggedAccounts');
        if (savedFlaggedAccounts) {
          setFlaggedAccounts(JSON.parse(savedFlaggedAccounts));
        } else {
          setFlaggedAccounts([]);
        }
      } catch (error) {
        console.error('Error loading admin data:', error);
      }
    };
    loadData();
    // Set up interval to refresh orders every 5 seconds
    const intervalId = setInterval(async () => {
      try {
        const ordersData = await AdminService.getOrders();
        setOrders(ordersData);
        const stats = await AdminService.getDashboardStats();
        setDashboardStats(stats);
      } catch (error) {
        console.error('Error refreshing orders:', error);
      }
    }, 5000);
    return () => clearInterval(intervalId);
  }, [isLoggedIn, user, isLoaded]);
  // Toggle sidebar for mobile
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  // Handle order status change
  const handleOrderStatusChange = async (orderId, newStatus) => {
    try {
      await AdminService.updateOrderStatus(orderId, newStatus);
      setOrders(orders.map(order => order.OrderID === orderId ? {
        ...order,
        Order_Status: newStatus
      } : order));
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };
  // Handle editing menu item
  const startEditingItem = item => {
    setEditingItem({
      ...item
    });
  };
  const cancelEditingItem = () => {
    setEditingItem(null);
  };
  const saveEditedItem = async () => {
    try {
      if (!editingItem) return;
      await AdminService.updateMenuItem(editingItem);
      setMenuItems(menuItems.map(item => item.ItemID === editingItem.ItemID ? editingItem : item));
      setEditingItem(null);
      alert('Menu item updated successfully!');
    } catch (error) {
      console.error('Error saving menu item:', error);
      alert('Failed to update menu item');
    }
  };
  const handleItemChange = e => {
    if (!editingItem) return;
    const {
      name,
      value
    } = e.target;
    setEditingItem({
      ...editingItem,
      [name]: name === 'Price' ? parseFloat(value) || 0 : value
    });
  };
  // Handle adding new menu item
  const handleNewItemChange = e => {
    const {
      name,
      value
    } = e.target;
    setNewItem({
      ...newItem,
      [name]: name === 'price' ? value === '' ? '' : parseFloat(value) || 0 : value
    });
  };
  const addNewItem = async () => {
    if (!newItem.name || !newItem.category || !newItem.price || !newItem.image) {
      alert('Please fill out all required fields');
      return;
    }
    try {
      const newMenuItem: Omit<MenuItem, 'ItemID'> = {
        Item_Name: newItem.name,
        Category: newItem.category,
        Price: parseFloat(newItem.price as string),
        Availability: newItem.status as 'Available' | 'Low Stock' | 'Out of Stock',
        Description: newItem.description
      };
      await AdminService.addMenuItem(newMenuItem);
      const updatedItems = await AdminService.getMenuItems();
      setMenuItems(updatedItems);
      setNewItem({
        name: '',
        category: '',
        price: '',
        status: 'Available',
        image: '',
        description: ''
      });
      alert('Menu item added successfully!');
    } catch (error) {
      console.error('Error adding menu item:', error);
      alert('Failed to add menu item');
    }
  };
  // Handle deleting menu item
  const deleteMenuItem = async itemId => {
    if (window.confirm('Are you sure you want to delete this menu item?')) {
      try {
        await AdminService.deleteMenuItem(itemId);
        setMenuItems(menuItems.filter(item => item.ItemID !== itemId));
        alert('Menu item deleted successfully!');
      } catch (error) {
        console.error('Error deleting menu item:', error);
        alert('Failed to delete menu item');
      }
    }
  };
  // Handle editing promotion
  const startEditingPromotion = promo => {
    setEditingPromotion({
      ...promo
    });
  };
  const cancelEditingPromotion = () => {
    setEditingPromotion(null);
  };
  const saveEditedPromotion = async () => {
    try {
      if (!editingPromotion) return;
      await AdminService.updatePromotion(editingPromotion);
      setPromotions(promotions.map(promo => promo.PromoID === editingPromotion.PromoID ? editingPromotion : promo));
      setEditingPromotion(null);
      alert('Promotion updated successfully!');
    } catch (error) {
      console.error('Error saving promotion:', error);
      alert('Failed to update promotion');
    }
  };
  const handlePromotionChange = e => {
    if (!editingPromotion) return;
    const {
      name,
      value
    } = e.target;
    setEditingPromotion({
      ...editingPromotion,
      [name]: value
    });
  };
  // Handle adding new promotion
  const handleNewPromotionChange = e => {
    const {
      name,
      value
    } = e.target;
    setNewPromotion({
      ...newPromotion,
      [name]: name === 'value' ? value === '' ? '' : parseFloat(value) || 0 : value
    });
  };
  const addNewPromotion = async () => {
    if (!newPromotion.code || !newPromotion.description || !newPromotion.value) {
      alert('Please fill out all required fields');
      return;
    }
    try {
      const newPromo: Omit<PromoIncentive, 'PromoID'> = {
        Promo_Type: newPromotion.type as 'Discount' | 'Free Condiment' | 'Loyalty Reward',
        Description: newPromotion.description,
        Start_Date: new Date().toISOString().split('T')[0],
        End_Date: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0],
        Eligibility: newPromotion.usageLimit
      };
      await AdminService.addPromotion(newPromo);
      const updatedPromos = await AdminService.getPromotions();
      setPromotions(updatedPromos);
      setNewPromotion({
        code: '',
        description: '',
        type: 'Discount',
        value: '',
        usageLimit: 'One-time',
        status: 'Active'
      });
      alert('Promotion added successfully!');
    } catch (error) {
      console.error('Error adding promotion:', error);
      alert('Failed to add promotion');
    }
  };
  // Handle promotion deletion
  const deletePromotion = async promoId => {
    if (window.confirm('Are you sure you want to delete this promotion?')) {
      try {
        await AdminService.deletePromotion(promoId);
        setPromotions(promotions.filter(p => p.PromoID !== promoId));
        alert('Promotion deleted successfully!');
      } catch (error) {
        console.error('Error deleting promotion:', error);
        alert('Failed to delete promotion');
      }
    }
  };
  // Handle complaint status change
  const handleComplaintStatusChange = async (complaintId, newStatus) => {
    try {
      await AdminService.updateComplaintStatus(complaintId, newStatus);
      setComplaints(complaints.map(complaint => complaint.TicketNum === complaintId ? {
        ...complaint,
        Status: newStatus
      } : complaint));
    } catch (error) {
      console.error('Error updating complaint status:', error);
    }
  };
  // If not authenticated as admin, show loading or redirect
  if (!isLoggedIn || !user || user.User_type !== 'admin' || !isLoaded) {
    return <div className="flex justify-center items-center min-h-screen bg-gray-50" data-id="element-397">
        <div className="text-center" data-id="element-398">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-800 mx-auto" data-id="element-399"></div>
          <p className="mt-4 text-gray-700" data-id="element-400">Loading admin dashboard...</p>
        </div>
      </div>;
  }
  return <div className="bg-gray-50 min-h-screen" data-id="element-401">
      {/* Mobile header with menu button */}
      <div className="lg:hidden bg-gray-800 text-white p-4 flex justify-between items-center sticky top-0 z-20" data-id="element-402">
        <h1 className="text-xl font-semibold" data-id="element-403">Admin Dashboard</h1>
        <button onClick={toggleSidebar} className="p-2 rounded-md hover:bg-gray-700 focus:outline-none" aria-label="Toggle sidebar" data-id="element-404">
          <MenuIcon className="h-6 w-6" data-id="element-405" />
        </button>
      </div>
      <div className="flex" data-id="element-406">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transform transition-transform duration-300 ease-in-out fixed lg:sticky top-0 inset-y-0 left-0 z-30 w-64 bg-gray-800 h-screen overflow-y-auto`} data-id="element-407">
          <div className="p-4 hidden lg:block" data-id="element-408">
            <h1 className="text-white text-xl font-semibold" data-id="element-409">
              Admin Dashboard
            </h1>
          </div>
          <div className="p-4 flex justify-between items-center lg:hidden" data-id="element-410">
            <h2 className="text-white text-lg font-medium" data-id="element-411">Menu</h2>
            <button onClick={toggleSidebar} className="text-gray-300 hover:text-white" aria-label="Close sidebar" data-id="element-412">
              <XIcon className="h-6 w-6" data-id="element-413" />
            </button>
          </div>
          <nav className="mt-4" data-id="element-414">
            <button onClick={() => {
            setActiveTab('dashboard');
            setSidebarOpen(false);
          }} className={`flex items-center px-4 py-3 w-full text-left transition-colors ${activeTab === 'dashboard' ? 'bg-gray-900 text-white border-l-4 border-orange-500' : 'text-gray-300 hover:bg-gray-700'}`} data-id="element-415">
              <BarChartIcon className="h-5 w-5 mr-3" data-id="element-416" />
              Dashboard
            </button>
            <button onClick={() => {
            setActiveTab('orders');
            setSidebarOpen(false);
          }} className={`flex items-center px-4 py-3 w-full text-left transition-colors ${activeTab === 'orders' ? 'bg-gray-900 text-white border-l-4 border-orange-500' : 'text-gray-300 hover:bg-gray-700'}`} data-id="element-417">
              <PackageIcon className="h-5 w-5 mr-3" data-id="element-418" />
              Orders
            </button>
            <button onClick={() => {
            setActiveTab('menu');
            setSidebarOpen(false);
          }} className={`flex items-center px-4 py-3 w-full text-left transition-colors ${activeTab === 'menu' ? 'bg-gray-900 text-white border-l-4 border-orange-500' : 'text-gray-300 hover:bg-gray-700'}`} data-id="element-419">
              <UsersIcon className="h-5 w-5 mr-3" data-id="element-420" />
              Menu Management
            </button>
            <button onClick={() => {
            setActiveTab('promotions');
            setSidebarOpen(false);
          }} className={`flex items-center px-4 py-3 w-full text-left transition-colors ${activeTab === 'promotions' ? 'bg-gray-900 text-white border-l-4 border-orange-500' : 'text-gray-300 hover:bg-gray-700'}`} data-id="element-421">
              <TagIcon className="h-5 w-5 mr-3" data-id="element-422" />
              Promotions
            </button>
            <button onClick={() => {
            setActiveTab('users');
            setSidebarOpen(false);
          }} className={`flex items-center px-4 py-3 w-full text-left transition-colors ${activeTab === 'users' ? 'bg-gray-900 text-white border-l-4 border-orange-500' : 'text-gray-300 hover:bg-gray-700'}`} data-id="element-423">
              <UsersIcon className="h-5 w-5 mr-3" data-id="element-424" />
              Users
            </button>
            <button onClick={() => {
            setActiveTab('complaints');
            setSidebarOpen(false);
          }} className={`flex items-center px-4 py-3 w-full text-left transition-colors ${activeTab === 'complaints' ? 'bg-gray-900 text-white border-l-4 border-orange-500' : 'text-gray-300 hover:bg-gray-700'}`} data-id="element-425">
              <ShieldAlertIcon className="h-5 w-5 mr-3" data-id="element-426" />
              Complaints
            </button>
          </nav>
        </div>
        {/* Backdrop for mobile sidebar */}
        {sidebarOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden" onClick={toggleSidebar} aria-hidden="true" data-id="element-427"></div>}
        {/* Main Content */}
        <div className="flex-1 p-4 sm:p-6 lg:p-8" data-id="element-428">
          {activeTab === 'dashboard' && <div data-id="element-429">
              <h1 className="text-2xl font-semibold text-gray-800 mb-6" data-id="element-430">
                Dashboard Overview
              </h1>
              {/* Stats Overview */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-431">
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6" data-id="element-432">
                  <h2 className="text-sm font-medium text-gray-500" data-id="element-433">
                    Total Orders Today
                  </h2>
                  <p className="text-2xl sm:text-3xl font-semibold text-gray-900 mt-2" data-id="element-434">
                    {dashboardStats.todayOrderCount}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6" data-id="element-435">
                  <h2 className="text-sm font-medium text-gray-500" data-id="element-436">
                    Total Revenue Today
                  </h2>
                  <p className="text-2xl sm:text-3xl font-semibold text-gray-900 mt-2" data-id="element-437">
                    ₱{dashboardStats.totalRevenue.toFixed(2)}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6" data-id="element-438">
                  <h2 className="text-sm font-medium text-gray-500" data-id="element-439">
                    Pending Orders
                  </h2>
                  <p className="text-2xl sm:text-3xl font-semibold text-gray-900 mt-2" data-id="element-440">
                    {dashboardStats.pendingOrderCount}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6" data-id="element-441">
                  <h2 className="text-sm font-medium text-gray-500" data-id="element-442">
                    Total Users
                  </h2>
                  <p className="text-2xl sm:text-3xl font-semibold text-gray-900 mt-2" data-id="element-443">
                    {users.length}
                  </p>
                </div>
              </div>
              {/* Recent Orders */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6" data-id="element-444">
                <div className="flex justify-between items-center mb-6" data-id="element-445">
                  <h2 className="text-lg font-semibold text-gray-800" data-id="element-446">
                    Recent Orders
                  </h2>
                  <button onClick={() => setActiveTab('orders')} className="text-orange-600 hover:text-orange-800" data-id="element-447">
                    View All
                  </button>
                </div>
                {orders.length === 0 ? <p className="text-gray-500 text-center py-8" data-id="element-448">
                    No orders yet. Orders from customers will appear here.
                  </p> : <div className="overflow-x-auto" data-id="element-449">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-450">
                      <thead className="bg-gray-50" data-id="element-451">
                        <tr data-id="element-452">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-453">
                            Order ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-454">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-455">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-456">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-457">
                        {orders.slice(0, 5).map(order => <tr key={order.OrderID} data-id="element-458">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-459">
                              {order.OrderID}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-460">
                              {new Date(order.Order_Date).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-461">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${order.Order_Status === 'Delivered' ? 'bg-green-100 text-green-800' : order.Order_Status === 'Preparing' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-462">
                                {order.Order_Status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-463">
                              ₱{order.Total_Amount.toFixed(2)}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>}
              </div>
            </div>}
          {activeTab === 'orders' && <div data-id="element-464">
              <h1 className="text-2xl font-semibold text-gray-800 mb-6" data-id="element-465">
                Order Management
              </h1>
              {editingOrder ?
          // Order Edit View
          <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-466">
                  <div className="flex justify-between items-center mb-6" data-id="element-467">
                    <div data-id="element-468">
                      <h2 className="text-xl font-semibold text-gray-800" data-id="element-469">
                        Edit Order #{editingOrder.OrderID}
                      </h2>
                      <p className="text-sm text-gray-600" data-id="element-470">
                        Customer ID: {editingOrder.User_ID} | Date:{' '}
                        {new Date(editingOrder.Order_Date).toLocaleString()}
                      </p>
                    </div>
                    <button onClick={cancelOrderEditing} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-471">
                      Back to Orders
                    </button>
                  </div>
                  {/* Current Order Items */}
                  <div className="mb-6" data-id="element-472">
                    <h3 className="text-lg font-medium text-gray-800 mb-3" data-id="element-473">
                      Order Items
                    </h3>
                    <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-474">
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-475">
                        <thead className="bg-gray-50" data-id="element-476">
                          <tr data-id="element-477">
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-478">
                              Item
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-479">
                              Price
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-480">
                              Quantity
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-481">
                              Subtotal
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-482">
                          {orderItems.map(item => <tr key={item.OrderDetail_ID} data-id="element-483">
                              <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-484">
                                {item.name}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500" data-id="element-485">
                                ₱{item.price.toFixed(2)}
                              </td>
                              <td className="px-6 py-4" data-id="element-486">
                                <div className="flex items-center space-x-2" data-id="element-487">
                                  <button onClick={() => updateOrderItemQuantity(item.OrderDetail_ID, item.Quantity - 1)} className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300" data-id="element-488">
                                    -
                                  </button>
                                  <span className="w-12 text-center" data-id="element-489">
                                    {item.Quantity}
                                  </span>
                                  <button onClick={() => updateOrderItemQuantity(item.OrderDetail_ID, item.Quantity + 1)} className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300" data-id="element-490">
                                    +
                                  </button>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900" data-id="element-491">
                                ₱{item.Subtotal.toFixed(2)}
                              </td>
                            </tr>)}
                        </tbody>
                        <tfoot className="bg-gray-50" data-id="element-492">
                          <tr data-id="element-493">
                            <td colSpan={3} className="px-6 py-4 text-right text-sm font-bold text-gray-900" data-id="element-494">
                              Total
                            </td>
                            <td className="px-6 py-4 text-sm font-bold text-gray-900" data-id="element-495">
                              ₱{editingOrder.Total_Amount.toFixed(2)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                  {/* Add Items Section */}
                  <div className="mb-6" data-id="element-496">
                    <h3 className="text-lg font-medium text-gray-800 mb-3" data-id="element-497">
                      Add Items to Order
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-498">
                      {availableMenuItems.filter(menuItem => !orderItems.some(orderItem => orderItem.Item_ID === menuItem.ItemID)).map(item => <div key={item.ItemID} className="border border-gray-200 rounded-md p-4" data-id="element-499">
                            <h4 className="font-medium text-gray-900" data-id="element-500">
                              {item.Item_Name}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1" data-id="element-501">
                              {item.Category}
                            </p>
                            <div className="flex justify-between items-center mt-3" data-id="element-502">
                              <span className="font-semibold" data-id="element-503">
                                ₱{item.Price.toFixed(2)}
                              </span>
                              <button onClick={() => addItemToOrder(item.ItemID)} className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700" data-id="element-504">
                                Add
                              </button>
                            </div>
                          </div>)}
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3" data-id="element-505">
                    <button onClick={cancelOrderEditing} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-506">
                      Cancel
                    </button>
                    <button onClick={saveOrderChanges} className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700" data-id="element-507">
                      Save Changes
                    </button>
                  </div>
                </div> :
          // Orders List View
          <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-508">
                  {orders.length === 0 ? <div className="p-8 text-center" data-id="element-509">
                      <PackageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-510" />
                      <p className="text-gray-500" data-id="element-511">
                        No orders yet. Customer orders will appear here.
                      </p>
                    </div> : <div className="overflow-x-auto" data-id="element-512">
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-513">
                        <thead className="bg-gray-50" data-id="element-514">
                          <tr data-id="element-515">
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-516">
                              Order ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-517">
                              User ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-518">
                              Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-519">
                              Total
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-520">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-521">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-522">
                          {orders.map(order => <tr key={order.OrderID} data-id="element-523">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-524">
                                {order.OrderID}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-525">
                                {order.User_ID}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-526">
                                {order.Order_Date}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-527">
                                ₱{order.Total_Amount.toFixed(2)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-528">
                                <select className="border border-gray-300 rounded-md px-2 py-1 text-sm" value={order.Order_Status} onChange={e => handleOrderStatusChange(order.OrderID, e.target.value)} data-id="element-529">
                                  <option value="Pending" data-id="element-530">Pending</option>
                                  <option value="Preparing" data-id="element-531">Preparing</option>
                                  <option value="Delivering" data-id="element-532">Delivering</option>
                                  <option value="Delivered" data-id="element-533">Delivered</option>
                                  <option value="Cancelled" data-id="element-534">Cancelled</option>
                                </select>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-535">
                                <button onClick={() => loadOrderForEditing(order.OrderID)} className="text-blue-600 hover:text-blue-800" data-id="element-536">
                                  <PencilIcon className="h-5 w-5" data-id="element-537" />
                                </button>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}
                </div>}
            </div>}
          {activeTab === 'menu' && <div data-id="element-538">
              <div className="flex justify-between items-center mb-6" data-id="element-539">
                <h1 className="text-2xl font-semibold text-gray-800" data-id="element-540">
                  Menu Management
                </h1>
              </div>
              {/* Menu Items Table */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8" data-id="element-541">
                <div className="overflow-x-auto" data-id="element-542">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-543">
                    <thead className="bg-gray-50" data-id="element-544">
                      <tr data-id="element-545">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-546">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-547">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-548">
                          Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-549">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-550">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-551">
                      {menuItems.map(item => <tr key={item.ItemID} data-id="element-552">
                          {editingItem && editingItem.ItemID === item.ItemID ? <>
                              <td className="px-6 py-4" data-id="element-553">
                                <input type="text" name="Item_Name" value={editingItem.Item_Name} onChange={handleItemChange} className="w-full px-2 py-1 border border-gray-300 rounded-md" data-id="element-554" />
                              </td>
                              <td className="px-6 py-4" data-id="element-555">
                                <select name="Category" value={editingItem.Category} onChange={handleItemChange} className="w-full px-2 py-1 border border-gray-300 rounded-md" data-id="element-556">
                                  <option value="Main Dishes" data-id="element-557">
                                    Main Dishes
                                  </option>
                                  <option value="Side Dishes" data-id="element-558">
                                    Side Dishes
                                  </option>
                                  <option value="Desserts" data-id="element-559">Desserts</option>
                                  <option value="Beverages" data-id="element-560">Beverages</option>
                                </select>
                              </td>
                              <td className="px-6 py-4" data-id="element-561">
                                <input type="number" name="Price" value={editingItem.Price} onChange={handleItemChange} className="w-24 px-2 py-1 border border-gray-300 rounded-md" step="0.01" data-id="element-562" />
                              </td>
                              <td className="px-6 py-4" data-id="element-563">
                                <select name="Availability" value={editingItem.Availability} onChange={handleItemChange} className="w-full px-2 py-1 border border-gray-300 rounded-md" data-id="element-564">
                                  <option value="Available" data-id="element-565">Available</option>
                                  <option value="Low Stock" data-id="element-566">Low Stock</option>
                                  <option value="Out of Stock" data-id="element-567">
                                    Out of Stock
                                  </option>
                                </select>
                              </td>
                              <td className="px-6 py-4" data-id="element-568">
                                <button onClick={saveEditedItem} className="text-green-600 hover:text-green-800 mr-2" data-id="element-569">
                                  <SaveIcon className="h-5 w-5" data-id="element-570" />
                                </button>
                                <button onClick={cancelEditingItem} className="text-red-600 hover:text-red-800" data-id="element-571">
                                  <XIcon className="h-5 w-5" data-id="element-572" />
                                </button>
                              </td>
                            </> : <>
                              <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-573">
                                {item.Item_Name}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500" data-id="element-574">
                                {item.Category}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500" data-id="element-575">
                                ₱{item.Price.toFixed(2)}
                              </td>
                              <td className="px-6 py-4" data-id="element-576">
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${item.Availability === 'Available' ? 'bg-green-100 text-green-800' : item.Availability === 'Low Stock' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`} data-id="element-577">
                                  {item.Availability}
                                </span>
                              </td>
                              <td className="px-6 py-4" data-id="element-578">
                                <button onClick={() => startEditingItem(item)} className="text-blue-600 hover:text-blue-800 mr-2" data-id="element-579">
                                  <PencilIcon className="h-5 w-5" data-id="element-580" />
                                </button>
                                <button onClick={() => deleteMenuItem(item.ItemID)} className="text-red-600 hover:text-red-800" data-id="element-581">
                                  <TrashIcon className="h-5 w-5" data-id="element-582" />
                                </button>
                              </td>
                            </>}
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Add New Item Form */}
              <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-583">
                <h2 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-584">
                  Add New Menu Item
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-585">
                  <div data-id="element-586">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-587">
                      Name*
                    </label>
                    <input type="text" name="name" value={newItem.name} onChange={handleNewItemChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" data-id="element-588" />
                  </div>
                  <div data-id="element-589">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-590">
                      Category*
                    </label>
                    <select name="category" value={newItem.category} onChange={handleNewItemChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" data-id="element-591">
                      <option value="" data-id="element-592">Select category</option>
                      <option value="Main Dishes" data-id="element-593">Main Dishes</option>
                      <option value="Side Dishes" data-id="element-594">Side Dishes</option>
                      <option value="Desserts" data-id="element-595">Desserts</option>
                      <option value="Beverages" data-id="element-596">Beverages</option>
                    </select>
                  </div>
                  <div data-id="element-597">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-598">
                      Price (₱)*
                    </label>
                    <input type="number" name="price" value={newItem.price} onChange={handleNewItemChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" step="0.01" data-id="element-599" />
                  </div>
                  <div data-id="element-600">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-601">
                      Status
                    </label>
                    <select name="status" value={newItem.status} onChange={handleNewItemChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" data-id="element-602">
                      <option value="Available" data-id="element-603">Available</option>
                      <option value="Low Stock" data-id="element-604">Low Stock</option>
                      <option value="Out of Stock" data-id="element-605">Out of Stock</option>
                    </select>
                  </div>
                  <div className="md:col-span-2" data-id="element-606">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-607">
                      Image URL*
                    </label>
                    <input type="text" name="image" value={newItem.image} onChange={handleNewItemChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="https://example.com/image.jpg" data-id="element-608" />
                  </div>
                  <div className="md:col-span-2" data-id="element-609">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-610">
                      Description
                    </label>
                    <textarea name="description" value={newItem.description} onChange={handleNewItemChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md" data-id="element-611" />
                  </div>
                </div>
                <div className="mt-6 flex justify-end" data-id="element-612">
                  <button onClick={addNewItem} className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 flex items-center" data-id="element-613">
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-614" />
                    Add Item
                  </button>
                </div>
              </div>
            </div>}
          {activeTab === 'promotions' && <div data-id="element-615">
              <h1 className="text-2xl font-semibold text-gray-800 mb-6" data-id="element-616">
                Promotions Management
              </h1>
              {/* Promotions Table */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8" data-id="element-617">
                <div className="overflow-x-auto" data-id="element-618">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-619">
                    <thead className="bg-gray-50" data-id="element-620">
                      <tr data-id="element-621">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-622">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-623">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-624">
                          Start Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-625">
                          End Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-626">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-627">
                      {promotions.map(promo => <tr key={promo.PromoID} data-id="element-628">
                          {editingPromotion && editingPromotion.PromoID === promo.PromoID ? <>
                              <td className="px-6 py-4" data-id="element-629">
                                <select name="Promo_Type" value={editingPromotion.Promo_Type} onChange={handlePromotionChange} className="w-full px-2 py-1 border border-gray-300 rounded-md" data-id="element-630">
                                  <option value="Discount" data-id="element-631">Discount</option>
                                  <option value="Free Condiment" data-id="element-632">
                                    Free Condiment
                                  </option>
                                  <option value="Loyalty Reward" data-id="element-633">
                                    Loyalty Reward
                                  </option>
                                </select>
                              </td>
                              <td className="px-6 py-4" data-id="element-634">
                                <input type="text" name="Description" value={editingPromotion.Description} onChange={handlePromotionChange} className="w-full px-2 py-1 border border-gray-300 rounded-md" data-id="element-635" />
                              </td>
                              <td className="px-6 py-4" data-id="element-636">
                                <input type="date" name="Start_Date" value={editingPromotion.Start_Date} onChange={handlePromotionChange} className="w-full px-2 py-1 border border-gray-300 rounded-md" data-id="element-637" />
                              </td>
                              <td className="px-6 py-4" data-id="element-638">
                                <input type="date" name="End_Date" value={editingPromotion.End_Date} onChange={handlePromotionChange} className="w-full px-2 py-1 border border-gray-300 rounded-md" data-id="element-639" />
                              </td>
                              <td className="px-6 py-4" data-id="element-640">
                                <button onClick={saveEditedPromotion} className="text-green-600 hover:text-green-800 mr-2" data-id="element-641">
                                  <SaveIcon className="h-5 w-5" data-id="element-642" />
                                </button>
                                <button onClick={cancelEditingPromotion} className="text-red-600 hover:text-red-800" data-id="element-643">
                                  <XIcon className="h-5 w-5" data-id="element-644" />
                                </button>
                              </td>
                            </> : <>
                              <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-645">
                                {promo.Promo_Type}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500" data-id="element-646">
                                {promo.Description}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500" data-id="element-647">
                                {promo.Start_Date}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500" data-id="element-648">
                                {promo.End_Date}
                              </td>
                              <td className="px-6 py-4" data-id="element-649">
                                <button onClick={() => startEditingPromotion(promo)} className="text-blue-600 hover:text-blue-800 mr-2" data-id="element-650">
                                  <PencilIcon className="h-5 w-5" data-id="element-651" />
                                </button>
                                <button onClick={() => deletePromotion(promo.PromoID)} className="text-red-600 hover:text-red-800" data-id="element-652">
                                  <TrashIcon className="h-5 w-5" data-id="element-653" />
                                </button>
                              </td>
                            </>}
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Add New Promotion Form */}
              <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-654">
                <h2 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-655">
                  Create New Promotion
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-656">
                  <div data-id="element-657">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-658">
                      Promo Code*
                    </label>
                    <input type="text" name="code" value={newPromotion.code} onChange={handleNewPromotionChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="e.g. SUMMER2024" data-id="element-659" />
                  </div>
                  <div data-id="element-660">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-661">
                      Type
                    </label>
                    <select name="type" value={newPromotion.type} onChange={handleNewPromotionChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" data-id="element-662">
                      <option value="Discount" data-id="element-663">Discount</option>
                      <option value="Free Condiment" data-id="element-664">Free Condiment</option>
                      <option value="Loyalty Reward" data-id="element-665">Loyalty Reward</option>
                    </select>
                  </div>
                  <div className="md:col-span-2" data-id="element-666">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-667">
                      Description*
                    </label>
                    <input type="text" name="description" value={newPromotion.description} onChange={handleNewPromotionChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="e.g. 10% off on all orders" data-id="element-668" />
                  </div>
                  <div data-id="element-669">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-670">
                      Value*
                    </label>
                    <input type="number" name="value" value={newPromotion.value} onChange={handleNewPromotionChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="10" data-id="element-671" />
                  </div>
                  <div data-id="element-672">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-673">
                      Usage Limit
                    </label>
                    <select name="usageLimit" value={newPromotion.usageLimit} onChange={handleNewPromotionChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" data-id="element-674">
                      <option value="One-time" data-id="element-675">One-time per customer</option>
                      <option value="Limited" data-id="element-676">Limited uses</option>
                      <option value="Unlimited" data-id="element-677">Unlimited</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6 flex justify-end" data-id="element-678">
                  <button onClick={addNewPromotion} className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 flex items-center" data-id="element-679">
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-680" />
                    Create Promotion
                  </button>
                </div>
              </div>
            </div>}
          {activeTab === 'users' && <div data-id="element-681">
              <h1 className="text-2xl font-semibold text-gray-800 mb-6" data-id="element-682">
                User Management
              </h1>
              <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-683">
                {users.length === 0 ? <div className="p-8 text-center" data-id="element-684">
                    <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-685" />
                    <p className="text-gray-500" data-id="element-686">
                      No users yet. Registered users will appear here.
                    </p>
                  </div> : <div className="overflow-x-auto" data-id="element-687">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-688">
                      <thead className="bg-gray-50" data-id="element-689">
                        <tr data-id="element-690">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-691">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-692">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-693">
                            Contact
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-694">
                            User Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-695">
                            Registered
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-696">
                        {users.map(user => <tr key={user.User_ID} data-id="element-697">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-698">
                              {user.Name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-699">
                              {user.Email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-700">
                              {user.Contact}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-701">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${user.User_type === 'admin' ? 'bg-purple-100 text-purple-800' : user.User_type === 'delivery' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-702">
                                {user.User_type}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-703">
                              {new Date(user.Date_Registered).toLocaleDateString()}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>}
              </div>
            </div>}
          {activeTab === 'complaints' && <div data-id="element-704">
              <h1 className="text-2xl font-semibold text-gray-800 mb-6" data-id="element-705">
                Complaints Management
              </h1>
              <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-706">
                {complaints.length === 0 ? <div className="p-8 text-center" data-id="element-707">
                    <ShieldAlertIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-708" />
                    <p className="text-gray-500" data-id="element-709">
                      No complaints yet. Customer complaints will appear here.
                    </p>
                  </div> : <div className="overflow-x-auto" data-id="element-710">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-711">
                      <thead className="bg-gray-50" data-id="element-712">
                        <tr data-id="element-713">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-714">
                            Ticket #
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-715">
                            User ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-716">
                            Order ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-717">
                            Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-718">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-719">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-720">
                        {complaints.map(complaint => <tr key={complaint.TicketNum} data-id="element-721">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-722">
                              #{complaint.TicketNum}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-723">
                              {complaint.User_ID}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-724">
                              {complaint.OrderID}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" data-id="element-725">
                              {complaint.Description}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-726">
                              {complaint.Date_Submitted}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-727">
                              <select className="border border-gray-300 rounded-md px-2 py-1 text-sm" value={complaint.Status} onChange={e => handleComplaintStatusChange(complaint.TicketNum, e.target.value)} data-id="element-728">
                                <option value="Open" data-id="element-729">Open</option>
                                <option value="In Progress" data-id="element-730">In Progress</option>
                                <option value="Resolved" data-id="element-731">Resolved</option>
                              </select>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>}
              </div>
            </div>}
        </div>
      </div>
    </div>;
};
export default AdminDashboard;